const AUTHORIZE_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';

/**
 * Where Microsoft sends the popup back to. A route on the SPA, not the API: the page there
 * posts the code to its opener, so no browser ever arrives at an API endpoint carrying one.
 * The API checks this against its own registered list before redeeming, because the same
 * value has to be quoted in a request that carries the client secret.
 */
export const REDIRECT_PATH = '/auth/microsoft';

const POPUP_FEATURES = 'width=520,height=640,menubar=no,toolbar=no,location=no,status=no';

/**
 * How long to wait, after the popup disappears, for a message it may already have queued
 * before calling the sign-in cancelled.
 */
const CLOSE_GRACE_MS = 400;

let clientIdPromise = null;

const randomToken = () => {
	const bytes = new Uint8Array(16);
	window.crypto.getRandomValues(bytes);
	return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const base64url = (bytes) => {
	let binary = '';
	for(const byte of new Uint8Array(bytes)) {
		binary += String.fromCharCode(byte);
	}
	return window.btoa(binary)
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '');
};

/**
 * A PKCE verifier and its challenge. The verifier stays in this tab and travels to our API with
 * the code; only the challenge goes to Microsoft, so a code lifted from the popup's own URL --
 * a history entry, a referrer, an extension -- cannot be redeemed by whoever found it.
 */
const pkcePair = async() => {
	const verifier = `${randomToken()}${randomToken()}${randomToken()}`;
	// The verifier is hex, so its bytes are its character codes — no `TextEncoder` needed, and
	// one less browser API to depend on (jsdom, for one, does not ship it).
	const bytes = Uint8Array.from(verifier, (character) => character.charCodeAt(0));
	const digest = await window.crypto.subtle.digest('SHA-256', bytes);

	return { verifier, challenge: base64url(digest) };
};

/**
 * Microsoft Entra ID sign-in, driven by hand rather than through MSAL.
 *
 * MSAL would add ~200KB and still need a registered redirect URI, so it buys little here: the
 * authorization-code flow is a URL, a popup, and a `postMessage` from the page Microsoft
 * returns to. Doing it directly also keeps the shape identical to the Google path — the browser
 * only ever handles a one-time code, and the API redeems it with the client secret.
 */
const MicrosoftIdentityService = {

	redirectUri: () => {
		return `${window.location.origin}${REDIRECT_PATH}`;
	},

	/**
	 * The public client ID, as configured on the API and delivered by the boot `system-check`
	 * request. Resolves to null when Microsoft sign-in is switched off.
	 *
	 * @returns {Promise<string|null>}
	 */
	clientId: () => {
		if(!clientIdPromise) {
			const ready = window.pixwel?.configReady ?? Promise.resolve();
			clientIdPromise = ready
				.catch(() => null)
				.then(() => window.pixwel?.microsoftClientId || null);
		}

		return clientIdPromise;
	},

	/**
	 * Opens Microsoft's account chooser in a popup and resolves with a one-time code.
	 *
	 * Call it straight from a click handler: the popup only survives the browser's popup
	 * blocker while the click's user activation is live, which is why the client ID is
	 * resolved on mount rather than on the way to opening one.
	 *
	 * @returns {Promise<{code: string, redirectUri: string, codeVerifier: string}>}
	 */
	requestCode: async() => {
		// Opened empty and *synchronously*, before anything is awaited. Safari and iOS only
		// permit a popup created within the click event itself, and there are two awaits below
		// — the client ID and the PKCE digest — either of which would put us outside it. So the
		// window is claimed first and navigated once there is somewhere to send it.
		const popup = window.open('', 'pixwel-microsoft-signin', POPUP_FEATURES);

		if(!popup) {
			throw new Error('microsoft-popup-blocked');
		}

		let clientId;
		let redirectUri;
		let state;
		let verifier;
		let challenge;

		try {
			clientId = await MicrosoftIdentityService.clientId();

			if(!clientId) {
				throw new Error('microsoft-not-configured');
			}

			redirectUri = MicrosoftIdentityService.redirectUri();
			state = randomToken();
			({ verifier, challenge } = await pkcePair());
		}
		catch(e) {
			// Nothing to send it to, so do not leave an empty window sitting there.
			try {
				popup.close();
			}
			catch{
				// Already gone.
			}
			throw e;
		}

		const params = new URLSearchParams({
			client_id: clientId,
			response_type: 'code',
			redirect_uri: redirectUri,
			response_mode: 'query',
			// `email` is what the API matches on, and Microsoft only emits `xms_edov` — the
			// claim that makes matching safe — when `email` is present. `profile` rides along
			// so the chooser shows a name.
			scope: 'openid email profile',
			// Always ask, rather than silently reusing whichever account the browser is already
			// signed into. Someone clicking this on a shared machine means to choose.
			prompt: 'select_account',
			state,
			code_challenge: challenge,
			code_challenge_method: 'S256'
		});

		// `replace` rather than assignment: it leaves no `about:blank` entry in the popup's
		// history, and it keeps eslint's `require-atomic-updates` quiet about writing to a
		// property after an await, which here is a const local nobody else can touch.
		popup.location.replace(`${AUTHORIZE_URL}?${params}`);

		return new Promise((resolve, reject) => {
			let settled = false;

			const finish = (fn, value) => {
				if(settled) {
					return;
				}
				settled = true;
				window.removeEventListener('message', onMessage);
				clearInterval(closedCheck);
				try {
					popup.close();
				}
				catch{
					// Already gone, which is the usual case.
				}
				fn(value);
			};

			const onMessage = (event) => {
				// Only our own callback page may speak here: it is the one that holds the code.
				if(event.origin !== window.location.origin || event.data?.source !== 'pixwel-microsoft-signin') {
					return;
				}
				// And only for the request we started — `state` is what ties the reply to it.
				if(event.data.state !== state) {
					return;
				}

				if(event.data.code) {
					finish(resolve, { code: event.data.code, redirectUri, codeVerifier: verifier });
					return;
				}

				finish(reject, new Error(event.data.error || 'microsoft-cancelled'));
			};

			window.addEventListener('message', onMessage);

			// Closing the window is how most people cancel, and it fires no event of its own.
			//
			// A closed window is not proof of a cancellation, though: `postMessage` queues
			// delivery, so a successful code can still be in flight when the popup goes away.
			// Rejecting the moment `closed` turns true would discard it intermittently — so the
			// grace period below gives a queued message time to arrive, and `finish()` ignores
			// whichever of the two comes second.
			const closedCheck = setInterval(() => {
				if(!popup.closed) {
					return;
				}

				clearInterval(closedCheck);
				setTimeout(() => finish(reject, new Error('microsoft-cancelled')), CLOSE_GRACE_MS);
			}, 500);
		});
	}
};

export default MicrosoftIdentityService;
